import React from "react";

import {
  StyleSheet,
  View,
  Dimensions,
  ScrollView,
  KeyboardAvoidingView,
} from "react-native";

import {
  hasItems,
  getStateData,
  getActivitiesWithTags,
  sortByRelevance,
  isWeb,
  escapeRegexp,
} from "../../../../helpers/utils";

import Header from "../../common/header";
import Search from "../../common/search-bar/Search";
import SearchFiltersSets from "../../common/search-bar/SearchFiltersSets";
import AppConfig from "../../../../config/AppConfig";
import ActivitiesList from "../../activities/ActivitiesList/ActivitiesList";
import ManaTextInput from "../../common/ManaTextInput";
import ImageComponent from "../../common/ImageComponent";
import ManaEmojiPicker from "../../common/emoji-picker/ManaEmojiPicker";
import appStyles from "../../../appStyles";
import AppMessages from "../../../../config/AppMessages";
import TagActivitiesFabButton from "./TagActivitiesFabButton";

const { height: HEIGHT, width: WIDTH } = Dimensions.get("window");

export default class TagActivitiesPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.timeout = 0;

    this.state = {
      isLoading: true,
      tag: {},
      openEmojiDialog: false,
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.refreshPage();
        },
      );
    } else {
      this.refreshPage();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route.params?.args;
    } else {
      return this.controller.state.screenArgs?.args?.args;
    }
  }

  refreshPage = async () => {
    const { tag } = this.getArgs() || {};

    if (!tag) {
      this.controller.changePage("feed", {
        selectedPage: "rn:activities",
        args: {},
      });
      return;
    }

    const sets = getStateData(this.controller.state, "sets", tag.sets, "name");
    const selectedSets = sets.filter((s) => s.isSelected).map((s) => s.id);
    const allActivities = this.getActivities(tag);

    const selectedActivitiesIds = allActivities
      .filter((a) => a.is_selected)
      .map((a) => a.id);

    this.setState((state) => ({
      ...state,
      isLoading: false,
      tag,
      sets,
      selectedSets,
      allActivities,
      selectedActivitiesIds: new Set(selectedActivitiesIds),
    }));
  };

  getActivities = (tag) => {
    let allActivities =
      [...getActivitiesWithTags(this.controller.state, [tag.edit_id])] || [];
    allActivities
      .filter((a) => !a.isHidden)
      .forEach((a) => {
        a.isHidden = !a.is_active;
        a.is_favorite = a.is_favorite || 0;
        a.is_selected = (a.tags || []).includes(tag.edit_id);
      });

    allActivities.sort((a, b) => {
      if (a.is_selected == b.is_selected) {
        if (a.is_favorite == b.is_favorite) {
          return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
        }
        return a.is_favorite < b.is_favorite ? 1 : -1;
      }

      return a.is_selected > b.is_selected ? -1 : 1;
    });
    return allActivities;
  };

  // ------------------------------------- FILTER -------------------------------------
  filterResults = ({
    term,
    showActivities = true,
    showFavorites = true,
    showArchived = false,
    sets = [],
  }) => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const { allActivities } = this.state;
    let filteredActivities = showActivities ? [...allActivities] : [];

    // Multi terms search
    const pattern = new RegExp(
      (escapeRegexp(term) || "").split(" ").join("|"),
      "gi",
    );
    filteredActivities.forEach((a) => {
      a.isHidden = !!term ? a.name && !a.name.match(pattern) : false;

      if (!showArchived && !a.is_active) a.isHidden = true;
      if (!showFavorites && a.is_favorite != 1) a.isHidden = true;

      if (!a.isHidden && hasItems(sets) && hasItems(a.sets)) {
        const selectedSets = a.sets.filter((setId) => sets.includes(setId));
        // No selected sets found
        if (!hasItems(selectedSets)) a.isHidden = false;
      }
    });

    const sortedActivities = sortByRelevance(filteredActivities, term);

    this.setState((state) => ({
      ...state,
      isLoading: false,
      allActivities: [...sortedActivities],
    }));
  };

  // ------------------------------------- UPDATE -------------------------------------
  updateTagName = (name) => {
    this.setState(
      (state) => ({
        ...state,
        tag: {
          ...this.state.tag,
          edit_name: name,
        },
      }),
      () => {
        // Auto save
        this.autoSaveTag();
      },
    );
  };

  updateSelectedSets = ({ sets }) => {
    this.setState(
      (state) => ({
        ...state,
        selectedSets: sets,
      }),
      () => {
        // Auto save
        this.autoSaveTag();
      },
    );
  };

  updateSelectedActivities = (activity, is_selected) => {
    const { selectedActivitiesIds } = this.state;

    if (is_selected) {
      selectedActivitiesIds.add(activity.id);
    } else {
      selectedActivitiesIds.delete(activity.id);
    }

    this.setState(
      (state) => ({
        ...state,
        selectedActivitiesIds,
      }),
      () => {
        // Auto save
        this.autoSaveTag();
      },
    );
  };

  autoSaveTag = () => {
    const { tag, selectedSets, selectedActivitiesIds } = this.state;

    if (this.timeout) clearTimeout(this.timeout);

    this.timeout = setTimeout(() => {
      // Validate
      if (!hasItems(selectedSets)) {
        this.setState((state) => ({
          ...state,
          message: AppMessages.CoreElementTagIsRequired,
        }));

        setTimeout(() => {
          this.setState((state) => ({ ...state, message: "" }));
        }, 3000);
        return;
      }

      this.setState((state) => ({ ...state, isLoading: true }));

      this.controller.tracking.track("Edit Tag");
      console.log("Edit Tag");
      this.controller.cardActionWithCallback(
        {
          action: "save",
          target: "tag",
          ...tag,
          activities: [...selectedActivitiesIds],
          sets: selectedSets,
        },
        () => {
          this.setState((state) => ({ ...state, isLoading: false }));
        },
      );
    }, 1000);
  };

  // ------------------------------------- EMOJI -------------------------------------
  toggleEmojiDialog = (isOpen = true) => {
    this.setState((state) => ({ ...state, openEmojiDialog: isOpen }));
  };

  updateEmoji = (emoji) => {
    this.setState(
      (state) => ({
        ...state,
        tag: {
          ...this.state.tag,
          edit_icon: emoji.unified || emoji.id,
        },
      }),
      () => {
        // Auto save
        this.autoSaveTag();
      },
    );
  };

  render() {
    const { allActivities, isLoading, tag, sets, openEmojiDialog } = this.state;
    const activities = (allActivities || []).filter((a) => !a.isHidden);

    return (
      <>
        <ManaEmojiPicker
          isOpen={openEmojiDialog}
          onConfirm={this.updateEmoji}
          onClose={() => this.toggleEmojiDialog(false)}
        />
        <KeyboardAvoidingView
          style={[
            appStyles.container,
            {
              height: isWeb ? HEIGHT : HEIGHT - 20,
            },
          ]}
          behavior="height"
        >
          <Header
            title="Edit Tag"
            controller={this.controller}
            returnPage="rn:activities"
            hideAvatar
            isLoading={isLoading}
          />

          <View style={styles.content}>
            <View style={styles.inputContainer}>
              <View style={[styles.imageContainer]}>
                <ImageComponent
                  styles={styles.icon}
                  icon={tag && tag.edit_icon}
                  onPress={() => this.toggleEmojiDialog(true)}
                />
              </View>

              <ManaTextInput
                style={{ paddingLeft: 50 }}
                placeholderTextColor="#9C9EB9"
                name={"tagNameEdit"}
                placeholder="Tag name"
                onTextChanged={this.updateTagName}
                value={tag.edit_name || ""}
              />
            </View>

            <ScrollView
              style={appStyles.scrollView}
              contentContainerStyle={appStyles.scrollViewContent}
            >
              {sets ? (
                <SearchFiltersSets
                  sets={sets}
                  title="Core Elements"
                  hideCollapse
                  initialNumToRender={4}
                  callback={this.updateSelectedSets}
                />
              ) : null}
              {hasItems(activities) ? (
                <ActivitiesList
                  title="All activities"
                  contentType="activities"
                  activities={activities}
                  initialNumToRender={10}
                  controller={this.controller}
                  onActivitySelected={this.updateSelectedActivities}
                  showSelector
                />
              ) : null}
            </ScrollView>

            <TagActivitiesFabButton
              mariOptions={{ mariMessage: this.state.message }}
            />

            <Search
              filterResults={this.filterResults}
              placeholder="Search Activities"
              controller={this.controller}
            />
          </View>
        </KeyboardAvoidingView>
      </>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    backgroundColor: "#FFF",
    borderTopLeftRadius: AppConfig.BORDER_RADIUS,
    borderTopRightRadius: AppConfig.BORDER_RADIUS,
    flex: 1,
  },
  inputContainer: {
    width: WIDTH,
    maxWidth: "100%",
    marginTop: 10,
    paddingHorizontal: 10,
    display: "flex",
    justifyContent: "center",
    flexDirection: "row",
  },

  imageContainer: {
    position: "absolute",
    left: 10,
    top: 0,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#FFF",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 100,
  },
  icon: {
    width: 30,
    height: 30,
  },
});
